import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { TablePage, TableRequest } from './softsee'

/** Protectee - /api/v2/protectee/*; perechile vechi sunt in `ProtecteeController`. */

function params(r: Omit<TableRequest, 'list'>): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export interface ProtecteeDeviceRow {
  deviceId: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  Denumire: string
  ownerId: number
  Nume: string | null
  Prenume: string | null
  UserLogat: string | null
  DeviceGroup: string | null
  device_status: number | null
  backup_sessions: number
  external_ip: string | null
  internal_ip: string | null
  city: string | null
  DataAccesareDM: string | null
  [key: string]: unknown
}

export function devicesTableQuery(r: Omit<TableRequest, 'list'>) {
  return queryOptions({
    queryKey: ['protectee', 'devices', r],
    queryFn: () => apiGet<TablePage<ProtecteeDeviceRow>>(`/protectee/devices-table?${params(r)}`),
  })
}

export function deviceTabsQuery(id: number) {
  return queryOptions({
    queryKey: ['protectee', 'device', id, 'tabs'],
    queryFn: () => apiGet<{ device: { id: number }; tabs: { key: string; label: string }[] }>(`/protectee/devices/${id}/tabs`),
  })
}

export interface ProtecteeSummary {
  status: string
  location: Record<'date' | 'logged_user' | 'external_ip' | 'internal_ip' | 'address' | 'city' | 'country' | 'latitude' | 'longitude' | 'isp', string | null>
  last_backup: { date: string; status: string; response: string } | null
}

export function deviceSummaryQuery(id: number) {
  return queryOptions({ queryKey: ['protectee', 'device', id, 'summary'], queryFn: () => apiGet<ProtecteeSummary>(`/protectee/devices/${id}/summary`) })
}

export function nestedTableQuery(id: number, kind: 'locations' | 'events' | 'backup-events', r: Omit<TableRequest, 'list'>) {
  return queryOptions({
    queryKey: ['protectee', 'device', id, kind, r],
    queryFn: () => apiGet<TablePage<Record<string, unknown>>>(`/protectee/devices/${id}/${kind}/table?${params(r)}`),
  })
}

export interface ProtecteeSettings {
  device: { id: number }
  device_status: number
  login_status: number
  actions: { email: string; interval: number; webcam: boolean; desktop: boolean; apps: boolean; send_email: boolean } | null
  intervals: number[]
}

export function deviceSettingsQuery(id: number) {
  return queryOptions({ queryKey: ['protectee', 'device', id, 'settings'], queryFn: () => apiGet<ProtecteeSettings>(`/protectee/devices/${id}/settings`) })
}

export interface ProtecteeLocationPoint {
  id: number
  date: string
  latitude: string
  longitude: string
  address: string | null
  city: string | null
  country: string | null
}

/** Toate pozitiile masinii (cronologic) + cheia Google, pentru harta din Location history. */
export interface ProtecteeLocationTrack {
  points: ProtecteeLocationPoint[]
  mapsKey: string
}

export function locationTrackQuery(id: number) {
  return queryOptions({
    queryKey: ['protectee', 'device', id, 'locations', 'track'],
    queryFn: () => apiGet<ProtecteeLocationTrack>(`/protectee/devices/${id}/locations/track`),
  })
}

export interface ProtecteeStolenReport {
  id: number
  date: string
  external_ip: string
  latitude: string
  longitude: string
  windows: string[]
  has_webcam: boolean
  has_screenshot: boolean
}

export interface ProtecteeStolenReports {
  reports: ProtecteeStolenReport[]
  total: number
  start: number
  length: number
}

export function stolenReportsQuery(id: number, start: number, length: number) {
  return queryOptions({
    queryKey: ['protectee', 'device', id, 'reports', start, length],
    queryFn: () => apiGet<ProtecteeStolenReports>(`/protectee/devices/${id}/reports?start=${start}&length=${length}`),
  })
}

/** URL-ul pozei (webcam|screenshot) unui raport, pentru <img src>. */
export function reportImageUrl(reportId: number, kind: 'webcam' | 'screenshot'): string {
  return `/api/v2/protectee/reports/${reportId}/image/${kind}`
}

export type WriteAnswer = { status: string; message: string; task?: number; profile?: number }

export function post(path: string, fields: Record<string, PostValue> = {}) {
  return apiPost<WriteAnswer>(path, fields)
}

/** Cere agentului arborele de foldere (comanda DTR) - pasul 4 din wizardul de backup. */
export function requestDeviceTree(device: number) {
  return apiPost<WriteAnswer>(`/protectee/devices/${device}/tree/request`, {})
}

export interface ProtecteeBackup {
  device: { id: number }
  tasks: { ID: number; name: string }[]
  device_profiles: { profile_id: number; name: string }[]
  company_profiles: { ID: number; name: string }[]
  destinations: { ID: number; name: string }[]
  days_of_week: Record<string, string>
  months: Record<string, string>
  order_types: Record<string, string>
}

export function backupQuery(id: number) {
  return queryOptions({ queryKey: ['protectee', 'device', id, 'backup'], queryFn: () => apiGet<ProtecteeBackup>(`/protectee/devices/${id}/backup`) })
}

export interface TreeNode {
  path: string
  name: string
  checked: boolean
  /** Sursa configurata dar negasita in arborele curent al masinii (folder sters/mutat). */
  missing?: boolean
  children: TreeNode[]
}

export interface TaskDetails {
  task: { ID: number; name: string; device_id: number; destination_id: number; path: string; user: string; has_password: boolean; destination_key: string }
  algorithm: Record<string, Record<string, string>>
  tree: TreeNode[]
}

export function taskQuery(task: number) {
  return queryOptions({ queryKey: ['protectee', 'task', task], queryFn: () => apiGet<TaskDetails>(`/protectee/tasks/${task}`) })
}

export interface ProfileDetails {
  profile: { ID: number; name: string; destination_id: number; path: string; user: string; destination_key: string }
  algorithm: Record<string, Record<string, string>>
  tree: TreeNode[]
}

export function profileQuery(profile: number, device: number) {
  return queryOptions({ queryKey: ['protectee', 'profile', profile, device], queryFn: () => apiGet<ProfileDetails>(`/protectee/profiles/${profile}/devices/${device}`) })
}
